import { makeAutoObservable } from "mobx";
import { IChangeable } from "../../../stores/types";
import { IPolicyAddressRequirements } from "../policy-types";

export class PolicyAddressRequirementsStore
    implements IChangeable<IPolicyAddressRequirements>
{
    private _model?: IPolicyAddressRequirements;
    private _changes: Partial<IPolicyAddressRequirements> = {};

    constructor() {
        makeAutoObservable(this, {}, { autoBind: true });
    }

    public setModel(model: IPolicyAddressRequirements) {
        this._model = { ...model };
        this._changes = {};
    }

    private _clearUnneededChanges() {
        Object.keys(this._changes).forEach((key) => {
            const theKey = key as keyof IPolicyAddressRequirements;
            if (this._changes[theKey] === this._model?.[theKey]) {
                delete this._changes[theKey];
            }
        });
    }

    public get email() {
        return (
            this._changes.email ??
            this._model?.email ??
            this.getDefaultAddressRequirements().email
        );
    }

    public get phone() {
        return (
            this._changes.phone ??
            this._model?.phone ??
            this.getDefaultAddressRequirements().phone
        );
    }

    public get country() {
        return (
            this._changes.country ??
            this._model?.country ??
            this.getDefaultAddressRequirements().country
        );
    }

    public get state() {
        return (
            this._changes.state ??
            this._model?.state ??
            this.getDefaultAddressRequirements().state
        );
    }

    public get address_line() {
        return (
            this._changes.address_line ??
            this._model?.address_line ??
            this.getDefaultAddressRequirements().address_line
        );
    }

    public get apartment() {
        return (
            this._changes.apartment ??
            this._model?.apartment ??
            this.getDefaultAddressRequirements().apartment
        );
    }

    public get zip() {
        return (
            this._changes.zip ??
            this._model?.zip ??
            this.getDefaultAddressRequirements().zip
        );
    }

    public get city() {
        return (
            this._changes.city ??
            this._model?.city ??
            this.getDefaultAddressRequirements().city
        );
    }

    // Setters for each field
    public setEmailDisplay(value: boolean) {
        this._changes.email = { ...this.email, display: value };
        this._clearUnneededChanges();
    }

    public setEmailRequired(value: boolean) {
        this._changes.email = { ...this.email, required: value };
        this._clearUnneededChanges();
    }

    public setPhoneDisplay(value: boolean) {
        this._changes.phone = { ...this.phone, display: value };
        this._clearUnneededChanges();
    }

    public setPhoneRequired(value: boolean) {
        this._changes.phone = { ...this.phone, required: value };
        this._clearUnneededChanges();
    }

    public setCountryDisplay(value: boolean) {
        this._changes.country = { ...this.country, display: value };
        this._clearUnneededChanges();
    }

    public setCountryRequired(value: boolean) {
        this._changes.country = { ...this.country, required: value };
        this._clearUnneededChanges();
    }

    public setStateDisplay(value: boolean) {
        this._changes.state = { ...this.state, display: value };
        this._clearUnneededChanges();
    }

    public setStateRequired(value: boolean) {
        this._changes.state = { ...this.state, required: value };
        this._clearUnneededChanges();
    }

    public setAddressLineDisplay(value: boolean) {
        this._changes.address_line = { ...this.address_line, display: value };
        this._clearUnneededChanges();
    }

    public setAddressLineRequired(value: boolean) {
        this._changes.address_line = { ...this.address_line, required: value };
        this._clearUnneededChanges();
    }

    public setApartmentDisplay(value: boolean) {
        this._changes.apartment = { ...this.apartment, display: value };
        this._clearUnneededChanges();
    }

    public setZipDisplay(value: boolean) {
        this._changes.zip = { ...this.zip, display: value };
        this._clearUnneededChanges();
    }

    public setZipRequired(value: boolean) {
        this._changes.zip = { ...this.zip, required: value };
        this._clearUnneededChanges();
    }

    public setCityDisplay(value: boolean) {
        this._changes.city = { ...this.city, display: value };
        this._clearUnneededChanges();
    }

    public setCityRequired(value: boolean) {
        this._changes.city = { ...this.city, required: value };
        this._clearUnneededChanges();
    }

    public hasChanges(): boolean {
        return Object.keys(this._changes).length > 0;
    }

    public discardChanges(): void {
        this._changes = {};
    }

    public getUpdatedModel(): IPolicyAddressRequirements {
        return {
            ...(this._model ?? this.getDefaultAddressRequirements()),
            ...this._changes,
        };
    }

    public getDefaultAddressRequirements(): IPolicyAddressRequirements {
        return {
            email: { display: true, required: true },
            phone: { display: true, required: true },
            country: { display: true, required: true },
            state: { display: true, required: true },
            address_line: { display: true, required: true },
            apartment: { display: true },
            zip: { display: true, required: false },
            city: { display: true, required: true },
        };
    }
}
